/** Live acquisition and loss log from the worker tick, newest first. */
import { useMemo } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Trash2 } from 'lucide-react';
import { useUI } from '@/state/store';
import { Chip, TextButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useLoader } from '../../store';
import { fmtDurationMs, fmtUtc } from '../format';

export function AosLosPanel() {
  const dataset = useLoader((s) => s.dataset);
  const entries = useLoader((s) => s.aosLos);
  const filter = useLoader((s) => s.aosLosFilter);
  const setFilter = useLoader((s) => s.setAosLosFilter);
  const clear = useLoader((s) => s.clearAosLos);
  const activeContacts = useLoader((s) => s.activeContacts);
  const select = useUI((s) => s.select);

  const missionName = (id: string) => dataset?.missions.find((m) => m.id === id)?.shortName ?? id;
  const antennaName = (id: string) => dataset?.antennas.find((a) => a.id === id)?.shortName ?? id;

  const shown = useMemo(
    () => (filter ? entries.filter((e) => e.missionId === filter) : entries),
    [entries, filter],
  );
  const stats = useMemo(() => {
    const los = shown.filter((e) => e.type === 'LOS' && e.durationMs);
    const avg = los.length ? los.reduce((s, e) => s + (e.durationMs ?? 0), 0) / los.length : 0;
    return { aos: shown.filter((e) => e.type === 'AOS').length, los: los.length, avg };
  }, [shown]);

  const missionsSeen = useMemo(() => [...new Set(entries.map((e) => e.missionId))].sort(), [entries]);

  return (
    <div className="flex h-full flex-col text-data">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-hairline px-panel py-2">
        <select
          aria-label="Filter by mission"
          value={filter ?? ''}
          onChange={(e) => setFilter(e.target.value || null)}
          className="rounded-control border border-hairline bg-inset px-2 py-1 text-ink"
        >
          <option value="">All missions</option>
          {missionsSeen.map((id) => (
            <option key={id} value={id}>
              {missionName(id)}
            </option>
          ))}
        </select>
        <Chip tone="live">{activeContacts.length} active</Chip>
        <span className="text-ink-3">
          {stats.aos} AOS, {stats.los} LOS
          {stats.los > 0 ? `, average contact ${fmtDurationMs(stats.avg)}` : ''}
        </span>
        <TextButton onClick={clear} className="ml-auto flex items-center gap-1">
          <Trash2 aria-hidden className="h-3.5 w-3.5" /> Clear
        </TextButton>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        {activeContacts.length > 0 && (
          <div className="border-b border-hairline px-3 py-2">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">In contact now</div>
            <div className="flex flex-wrap gap-1">
              {activeContacts.map((c) => (
                <button
                  key={`${c.missionId}-${c.antennaId}`}
                  type="button"
                  onClick={() => select({ missionId: c.missionId, antennaId: null, assetId: null })}
                  className="rounded-control border border-live/40 bg-live/10 px-2 py-0.5 text-ink hover:border-live"
                >
                  {missionName(c.missionId)} <span className="text-ink-3">on</span> {antennaName(c.antennaId)}
                </button>
              ))}
            </div>
          </div>
        )}
        {shown.length === 0 ? (
          <div className="p-4 text-ink-3">
            No events yet. Events appear as missions rise above and set below each antenna's horizon on the
            shared clock.
          </div>
        ) : (
          <ul>
            {shown.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => select({ missionId: e.missionId, antennaId: null, assetId: null })}
                  className="flex w-full items-center gap-3 border-b border-hairline/60 px-panel py-cell-y text-left hover:bg-overlay focus-visible:outline-offset-[-2px]"
                >
                  {e.type === 'AOS' ? (
                    <ArrowUpFromLine aria-hidden className="h-3.5 w-3.5 text-live" />
                  ) : (
                    <ArrowDownToLine aria-hidden className="h-3.5 w-3.5 text-ink-3" />
                  )}
                  <span className={cn('w-10 font-mono', e.type === 'AOS' ? 'text-live' : 'text-ink-3')}>
                    {e.type}
                  </span>
                  <span className="font-mono text-ink-2">{fmtUtc(e.timeMs, true)}</span>
                  <span className="text-ink">{missionName(e.missionId)}</span>
                  <span className="text-ink-3">on</span>
                  <span className="text-ink">{antennaName(e.antennaId)}</span>
                  <span className="ml-auto font-mono text-ink-3">
                    {e.type === 'AOS'
                      ? `${e.elevation?.toFixed(1) ?? '?'} deg`
                      : `${fmtDurationMs(e.durationMs ?? 0)}, max ${Math.round(e.maxElevation ?? 0)} deg`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
